import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { assistantErrorText } from './assistantErrors';
import {
  X, Plus, ArrowLeft, Send, Loader, Trash2, MessageSquare,
  Database, HelpCircle, GitBranch, Code, Network, AlertTriangle, BarChart2, Zap,
  Check, XCircle, Play, Layers, Shield,
  type LucideIcon,
} from 'lucide-react';
import { useAssistantStore, useTenantConversations, AssistantMessage } from '../store/assistantStore';
import i18n from '../i18n';
import { useNavigationStore } from '../store/navigationStore';
import { useOntologyStore } from '../store/ontologyStore';
import { useProjectObjectTypes } from '../modules/files/projectPickers';
import { useLogicStore } from '../store/logicStore';
import { useAgentStore } from '../store/agentStore';
import { usePipelineStore } from '../store/pipelineStore';
import { useExplorerStore } from '../store/explorerStore';
import { useInsightStore } from '../store/insightStore';
import { getTenantId } from '../store/authStore';
import { uuid } from '../lib/uuid';
import { isAiLimited, parseAiLimit, aiLimitTitle, aiLimitMessage } from '../lib/aiLimits';
import { mix } from '../design-system/themeMix';
import { extractGuide, GUIDE_LANG } from './assistantGuide';
import { AssistantGuideCard } from './AssistantGuideCard';
import { withAlpha } from '../design-system/color';

const INFERENCE_URL    = import.meta.env.VITE_INFERENCE_SERVICE_URL     || 'http://localhost:8003';
const LOGIC_URL        = import.meta.env.VITE_LOGIC_SERVICE_URL         || 'http://localhost:8012';
const ONTOLOGY_URL     = import.meta.env.VITE_ONTOLOGY_SERVICE_URL      || 'http://localhost:8004';
const CONNECTOR_URL    = import.meta.env.VITE_CONNECTOR_SERVICE_URL     || 'http://localhost:8001';
const PIPELINE_URL     = import.meta.env.VITE_PIPELINE_SERVICE_URL      || 'http://localhost:8002';
const AGENT_URL        = import.meta.env.VITE_AGENT_SERVICE_URL         || 'http://localhost:8013';
const ALERT_URL        = import.meta.env.VITE_ALERT_ENGINE_URL          || 'http://localhost:8010';
const AUDIT_URL        = import.meta.env.VITE_AUDIT_SERVICE_URL         || 'http://localhost:8006';
const AUTH_URL         = import.meta.env.VITE_AUTH_SERVICE_URL          || 'http://localhost:8011';
const ADMIN_URL        = import.meta.env.VITE_ADMIN_SERVICE_URL         || 'http://localhost:8022';
const EVAL_URL         = import.meta.env.VITE_EVAL_SERVICE_URL          || 'http://localhost:8016';
const GATEWAY_URL      = import.meta.env.VITE_API_GATEWAY_URL           || 'http://localhost:8021';
const PROCESS_URL      = import.meta.env.VITE_PROCESS_ENGINE_URL        || 'http://localhost:8009';
const LINEAGE_URL      = import.meta.env.VITE_LINEAGE_SERVICE_URL       || 'http://localhost:8017';
const SEARCH_URL       = import.meta.env.VITE_SEARCH_SERVICE_URL        || 'http://localhost:8018';
const QUALITY_URL      = import.meta.env.VITE_DATA_QUALITY_SERVICE_URL  || 'http://localhost:8019';
const COLLAB_URL       = import.meta.env.VITE_COLLABORATION_SERVICE_URL || 'http://localhost:8020';

function fetchWithTimeout(url: string, opts: RequestInit, ms = 5000): Promise<any> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), ms);
  return fetch(url, { ...opts, signal: controller.signal })
    .then(r => r.json())
    .finally(() => clearTimeout(timer));
}

async function fetchLiveContext(currentPage: string) {
  const tenantId = getTenantId();
  const h = { 'x-tenant-id': tenantId };
  const opt = { headers: h };

  const [fnsRes, otsRes, connRes, pipRes, mpRes, runsRes] = await Promise.allSettled([
    fetchWithTimeout(`${LOGIC_URL}/logic/functions`, opt),
    fetchWithTimeout(`${ONTOLOGY_URL}/object-types`, opt),
    fetchWithTimeout(`${CONNECTOR_URL}/connectors`,  opt),
    fetchWithTimeout(`${PIPELINE_URL}/pipelines`,    opt),
    fetchWithTimeout(`${AGENT_URL}/model-providers`, opt),
    fetchWithTimeout(`${LOGIC_URL}/logic/runs?limit=20`, opt),
  ]);

  const functions: any[]    = fnsRes.status  === 'fulfilled' ? (fnsRes.value  || []) : [];
  const object_types: any[] = otsRes.status  === 'fulfilled' ? (otsRes.value  || []) : [];
  const rawConnectors: any[] = connRes.status === 'fulfilled' ? (connRes.value || []) : [];
  const pipelines: any[]    = pipRes.status  === 'fulfilled' ? (pipRes.value  || []) : [];
  const rawProviders: any[] = mpRes.status   === 'fulfilled' ? (mpRes.value   || []) : [];
  const rawRuns: any[]      = runsRes.status === 'fulfilled' ? (runsRes.value || []) : [];

  // Trim each run to a digestible summary — full trace is huge.
  const logic_runs = rawRuns.map((r: any) => ({
    id: r.id,
    function_id: r.function_id,
    function_version: r.function_version,
    status: r.status,
    triggered_by: r.triggered_by,
    started_at: r.started_at,
    finished_at: r.finished_at,
    error_preview: r.error ? String(r.error).slice(0, 200) : null,
  }));

  const model_providers = rawProviders.map((p: any) => ({
    id: p.id,
    name: p.name,
    provider_type: p.provider_type,
    base_url: p.base_url,
    has_api_key: p.has_api_key,
    models: p.models,
    is_default: p.is_default,
    enabled: p.enabled,
  }));

  const connectors = rawConnectors.map((c: any) => ({
    id: c.id,
    name: c.name,
    type: c.type,
    status: c.status,
    base_url: c.base_url,
    description: c.description,
    last_sync: c.last_sync,
    last_sync_row_count: c.last_sync_row_count,
    active_pipeline_count: c.active_pipeline_count,
    config: c.config ? {
      endpoint: c.config.endpoint,
      queryParams: c.config.queryParams,
    } : undefined,
    tags: c.tags,
  }));

  const functionsWithSchedules = await Promise.all(
    functions.map(async (fn: any) => {
      try {
        const s = await fetchWithTimeout(`${LOGIC_URL}/logic/functions/${fn.id}/schedules`, opt);
        return { ...fn, schedules: Array.isArray(s) ? s : [] };
      } catch { return { ...fn, schedules: [] }; }
    })
  );

  // Fetch sample records for the largest OTs by record count + always pin the
  // OTs referenced by the currently-open app (so debugging questions always
  // have schema + samples for the OT the user is actually looking at).
  //
  // Previously this was capped at the top 8 by record count, which made the
  // assistant blind to mid-size OTs the user was actively debugging and led
  // to "this OT has 0 records / no field X" hallucinations. 25 keeps token
  // cost bounded while covering the long tail.
  const recordsFetchLimit = 25;
  const sortedIds = [...object_types]
    .sort((a: any, b: any) => (b.total_records || 0) - (a.total_records || 0))
    .slice(0, recordsFetchLimit)
    .map((ot: any) => ot.id);
  const focusedOtIds = await fetchFocusedAppOtIds(currentPage, opt);
  const sampledIds = new Set<string>([...sortedIds, ...focusedOtIds]);
  const objectTypesWithRecords = await Promise.all(
    object_types.map(async (ot: any) => {
      if (!sampledIds.has(ot.id)) return { ...ot, recent_records: [], total_records: ot.total_records || 0 };
      try {
        const r = await fetchWithTimeout(
          `${ONTOLOGY_URL}/object-types/${ot.id}/records?limit=25`,
          opt, 8000
        );
        return { ...ot, recent_records: (r.records || []).slice(0, 25), total_records: r.total || 0 };
      } catch { return { ...ot, recent_records: [], total_records: ot.total_records || 0 }; }
    })
  );

  const current_app = await fetchCurrentAppConfig(currentPage, opt);
  const focused = collectFocusedEntities();

  return {
    current_page: currentPage,
    functions: functionsWithSchedules,
    object_types: objectTypesWithRecords,
    connectors,
    pipelines,
    model_providers,
    logic_runs,
    current_app,
    focused,
  };
}

// Read each module's "selected X" state from its zustand store so the
// assistant has full detail on whatever the user has open in the
// Logic / Agents / Pipelines / Ontology / Insights modules — not just
// apps. Detail-page routing isn't uniform across modules, but every
// editable module already maintains a `selected*` field locally.
function collectFocusedEntities(): Array<Record<string, unknown>> {
  const out: Array<Record<string, unknown>> = [];

  // Logic Studio: full function with blocks + schedules.
  const selectedFn = useLogicStore.getState().selectedFn;
  if (selectedFn) {
    out.push({
      kind: 'logic_function',
      id: selectedFn.id,
      name: selectedFn.name,
      status: selectedFn.status,
      input_schema: (selectedFn as any).input_schema || [],
      output_block: (selectedFn as any).output_block,
      blocks: ((selectedFn as any).blocks || []).map((b: any) => ({
        id: b.id, type: b.type,
        // Strip noise but keep the data the model needs to debug.
        config: Object.fromEntries(
          Object.entries(b).filter(([k]) => !['id', 'type'].includes(k)),
        ),
      })),
    });
  }

  // Agent Studio: full agent config (system prompt, tools, model).
  const selectedAgent = useAgentStore.getState().selectedAgent;
  if (selectedAgent) {
    out.push({
      kind: 'agent',
      id: (selectedAgent as any).id,
      name: (selectedAgent as any).name,
      model: (selectedAgent as any).model,
      system_prompt: (selectedAgent as any).system_prompt,
      tools: (selectedAgent as any).tools || [],
      triggers: (selectedAgent as any).triggers || [],
    });
  }

  // Pipeline editor: pipeline id, looked up from the in-memory list.
  const pipeStore = usePipelineStore.getState();
  const selectedPipelineId = (pipeStore as any).selectedPipelineId;
  if (selectedPipelineId) {
    const p = ((pipeStore as any).pipelines || []).find((x: any) => x.id === selectedPipelineId);
    if (p) {
      out.push({
        kind: 'pipeline',
        id: p.id,
        name: p.name,
        status: p.status,
        nodes: (p.nodes || []).map((n: any) => ({ id: n.id, type: n.type, config: n.config })),
        edges: p.edges || p.connections || [],
      });
    } else {
      out.push({ kind: 'pipeline', id: selectedPipelineId });
    }
  }

  // Ontology / data explorer: selected object type id.
  const selectedTypeId = useExplorerStore.getState().selectedTypeId;
  if (selectedTypeId) {
    out.push({ kind: 'object_type_focus', id: selectedTypeId });
  }

  // Insight detail drawer.
  const selectedInsight = useInsightStore.getState().selected;
  if (selectedInsight) {
    out.push({
      kind: 'insight',
      id: (selectedInsight as any).id,
      name: (selectedInsight as any).name || (selectedInsight as any).title,
      status: (selectedInsight as any).status,
      effect_size: (selectedInsight as any).effect_size,
      stability_score: (selectedInsight as any).stability_score,
    });
  }

  return out;
}

// When the user is editing an app (route `app-<id>`), pull its full config
// into the assistant context so debugging questions can quote exact field
// mappings and recordIdSource settings rather than guessing.
async function fetchCurrentAppConfig(currentPage: string, opt: RequestInit): Promise<any | null> {
  if (!currentPage || !currentPage.startsWith('app-')) return null;
  const appId = currentPage.slice('app-'.length);
  if (!appId) return null;
  try {
    const a = await fetchWithTimeout(
      `${ONTOLOGY_URL}/apps/${encodeURIComponent(appId)}`,
      opt, 5000
    );
    return a && a.id ? a : null;
  } catch { return null; }
}

async function fetchFocusedAppOtIds(currentPage: string, opt: RequestInit): Promise<string[]> {
  const app = await fetchCurrentAppConfig(currentPage, opt);
  if (!app) return [];
  const ids: string[] = [];
  if (Array.isArray(app.object_type_ids)) ids.push(...app.object_type_ids.filter(Boolean));
  if (app.object_type_id) ids.push(app.object_type_id);
  // Also harvest objectTypeIds referenced by actions / widgets in case the
  // top-level array missed any.
  const comps = Array.isArray(app.components) ? app.components : [];
  for (const c of comps) {
    if (c?.objectTypeId) ids.push(c.objectTypeId);
  }
  const actions = (app.settings?.actions || app.actions || []) as any[];
  for (const a of actions) {
    if (a?.objectTypeId) ids.push(a.objectTypeId);
  }
  return Array.from(new Set(ids.filter(Boolean)));
}

// ── Action types ─────────────────────────────────────────────────────────────
// The assistant supports a union of "custom" action types (with hand-rolled
// executors below) and "generic" action types (table-driven). Adding a generic
// action only requires adding a row to GENERIC_ACTION_REGISTRY.
type NexusActionType = string;

interface NexusAction {
  type: NexusActionType;
  name: string;
  summary: string[];
  payload: Record<string, unknown>;
}

// ── Generic (table-driven) action registry ──────────────────────────────────
// Each entry = label, icon, HTTP method + URL builder + body builder.
// The dispatcher in executeAction falls through to this table for any action
// type not handled by a custom branch above.
type ActionExecutor = {
  label: string;
  icon: React.ReactNode;
  method: 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  url: (p: Record<string, unknown>) => string;
  body?: (p: Record<string, unknown>) => Record<string, unknown> | null;
};

// Helper to strip used keys from payload when building a body.
const omit = (obj: Record<string, unknown>, keys: string[]) => {
  const out: Record<string, unknown> = {};
  for (const k of Object.keys(obj)) if (!keys.includes(k)) out[k] = obj[k];
  return out;
};

const GENERIC_ACTION_REGISTRY: Record<string, ActionExecutor> = {
  // ── Connectors lifecycle
  update_connector:    { label: 'Update Connector',    icon: <Database size={14}/>,    method: 'PUT',    url: p => `${CONNECTOR_URL}/connectors/${p.connector_id}`,                                  body: p => omit(p, ['connector_id']) },
  delete_connector:    { label: 'Delete Connector',    icon: <Trash2 size={14}/>,      method: 'DELETE', url: p => `${CONNECTOR_URL}/connectors/${p.connector_id}` },
  test_connector:      { label: 'Test Connector',      icon: <Check size={14}/>,       method: 'POST',   url: p => `${CONNECTOR_URL}/connectors/${p.connector_id}/test`,                              body: () => ({}) },

  // ── Pipelines lifecycle
  update_pipeline:     { label: 'Update Pipeline',     icon: <GitBranch size={14}/>,   method: 'PUT',    url: p => `${PIPELINE_URL}/pipelines/${p.pipeline_id}`,                                       body: p => omit(p, ['pipeline_id']) },
  delete_pipeline:     { label: 'Delete Pipeline',     icon: <Trash2 size={14}/>,      method: 'DELETE', url: p => `${PIPELINE_URL}/pipelines/${p.pipeline_id}` },

  // ── Pipeline schedules
  create_pipeline_schedule: { label: 'Create Pipeline Schedule', icon: <Play size={14}/>,   method: 'POST',   url: p => `${PIPELINE_URL}/pipelines/${p.pipeline_id}/schedules`,                       body: p => omit(p, ['pipeline_id']) },
  update_pipeline_schedule: { label: 'Update Pipeline Schedule', icon: <Play size={14}/>,   method: 'PUT',    url: p => `${PIPELINE_URL}/pipelines/${p.pipeline_id}/schedules/${p.schedule_id}`,      body: p => omit(p, ['pipeline_id', 'schedule_id']) },
  delete_pipeline_schedule: { label: 'Delete Pipeline Schedule', icon: <Trash2 size={14}/>, method: 'DELETE', url: p => `${PIPELINE_URL}/pipelines/${p.pipeline_id}/schedules/${p.schedule_id}` },
  run_pipeline_schedule_now:{ label: 'Run Schedule Now',         icon: <Play size={14}/>,   method: 'POST',   url: p => `${PIPELINE_URL}/pipelines/${p.pipeline_id}/schedules/${p.schedule_id}/run-now`, body: () => ({}) },

  // ── Object types & links
    // PATCH, no PUT: el PUT de ontologia es un REEMPLAZO TOTAL, y este body es
  // parcial (`omit` solo saca object_type_id), asi que un update de etiqueta
  // borraba properties, source_pipeline_id, position y los activity_*.
  update_object_type:  { label: 'Update Object Type',  icon: <Layers size={14}/>,      method: 'PATCH',  url: p => `${ONTOLOGY_URL}/object-types/${p.object_type_id}`,                                 body: p => omit(p, ['object_type_id']) },
  delete_object_type:  { label: 'Delete Object Type',  icon: <Trash2 size={14}/>,      method: 'DELETE', url: p => `${ONTOLOGY_URL}/object-types/${p.object_type_id}` },
  apply_enrichment:    { label: 'Apply Enrichment',    icon: <Layers size={14}/>,      method: 'POST',   url: p => `${ONTOLOGY_URL}/object-types/${p.object_type_id}/enrich`,                          body: p => p.proposal as Record<string, unknown> },
  delete_ontology_link:{ label: 'Delete Link',         icon: <Trash2 size={14}/>,      method: 'DELETE', url: p => `${ONTOLOGY_URL}/object-types/links/${p.link_id}` },

  // ── Logic functions
  update_logic_function:    { label: 'Update Logic Function',    icon: <Code size={14}/>,    method: 'PUT',    url: p => `${LOGIC_URL}/logic/functions/${p.function_id}`,                              body: p => omit(p, ['function_id']) },
  delete_logic_function:    { label: 'Delete Logic Function',    icon: <Trash2 size={14}/>,  method: 'DELETE', url: p => `${LOGIC_URL}/logic/functions/${p.function_id}` },
  publish_logic_function:   { label: 'Publish Logic Function',   icon: <Check size={14}/>,   method: 'POST',   url: p => `${LOGIC_URL}/logic/functions/${p.function_id}/publish`,                       body: () => ({}) },
  run_logic_function:       { label: 'Run Logic Function',       icon: <Play size={14}/>,    method: 'POST',   url: p => `${LOGIC_URL}/logic/functions/${p.function_id}/run`,                            body: p => ({ inputs: p.inputs || {} }) },
  create_logic_schedule:    { label: 'Create Logic Schedule',    icon: <Play size={14}/>,    method: 'POST',   url: p => `${LOGIC_URL}/logic/functions/${p.function_id}/schedules`,                     body: p => omit(p, ['function_id']) },
  delete_logic_schedule:    { label: 'Delete Logic Schedule',    icon: <Trash2 size={14}/>,  method: 'DELETE', url: p => `${LOGIC_URL}/logic/functions/${p.function_id}/schedules/${p.schedule_id}` },

  // ── Agents
  create_agent:             { label: 'Create Agent',             icon: <Zap size={14}/>,     method: 'POST',   url: () => `${AGENT_URL}/agents`,                                                          body: p => p },
  update_agent:             { label: 'Update Agent',             icon: <Zap size={14}/>,     method: 'PUT',    url: p => `${AGENT_URL}/agents/${p.agent_id}`,                                            body: p => omit(p, ['agent_id']) },
  delete_agent:             { label: 'Delete Agent',             icon: <Trash2 size={14}/>,  method: 'DELETE', url: p => `${AGENT_URL}/agents/${p.agent_id}` },
  set_agent_knowledge_scope:{ label: 'Set Agent Data Scope',     icon: <Zap size={14}/>,     method: 'PUT',    url: p => `${AGENT_URL}/agents/${p.agent_id}/knowledge-scope`,                            body: p => ({ scope: p.scope }) },
  create_agent_schedule:    { label: 'Create Agent Schedule',    icon: <Play size={14}/>,    method: 'POST',   url: p => `${AGENT_URL}/agents/${p.agent_id}/schedules`,                                  body: p => omit(p, ['agent_id']) },
  delete_agent_schedule:    { label: 'Delete Agent Schedule',    icon: <Trash2 size={14}/>,  method: 'DELETE', url: p => `${AGENT_URL}/agents/${p.agent_id}/schedules/${p.schedule_id}` },
  create_pipeline_trigger:  { label: 'Create Pipeline Trigger',  icon: <Network size={14}/>, method: 'POST',   url: () => `${AGENT_URL}/triggers`,                                                        body: p => p },
  test_fire_trigger:        { label: 'Test-Fire Trigger',        icon: <Play size={14}/>,    method: 'POST',   url: p => `${AGENT_URL}/triggers/${p.trigger_id}/test-fire`,                              body: () => ({}) },
  delete_pipeline_trigger:  { label: 'Delete Trigger',           icon: <Trash2 size={14}/>,  method: 'DELETE', url: p => `${AGENT_URL}/triggers/${p.trigger_id}` },

  // ── Action Catalog (typed agent-proposable writes)
  // Standalone actions in the Human Actions → Catalog. Distinct from
  // `create_app_action` which binds a form action to a specific app: these
  // are reusable across agents, logic functions, chat, and the catalog UI.
  // `update_action` and `delete_action` key on `name` (the unique slug).
  create_action:            { label: 'Create Action',            icon: <Shield size={14}/>,  method: 'POST',   url: () => `${ONTOLOGY_URL}/actions`,                                                       body: p => p },
  update_action:            { label: 'Update Action',            icon: <Shield size={14}/>,  method: 'PUT',    url: p => `${ONTOLOGY_URL}/actions/${p.name}`,                                              body: p => p },
  delete_action:            { label: 'Delete Action',            icon: <Trash2 size={14}/>,  method: 'DELETE', url: p => `${ONTOLOGY_URL}/actions/${p.name}` },
  // ── Human Action views (the card layout a reviewer sees per proposal).
  // `update_`/`delete_` key on `template_id`; the layout/condition contract is
  // validated server-side (422 with the reason).
  create_action_view_template: { label: 'Create Action View',      icon: <Shield size={14}/>,  method: 'POST',   url: () => `${ONTOLOGY_URL}/actions/_views/templates`,                                    body: p => p },
  update_action_view_template: { label: 'Update Action View',      icon: <Shield size={14}/>,  method: 'PUT',    url: p => `${ONTOLOGY_URL}/actions/_views/templates/${p.template_id}`,                    body: p => omit(p, ['template_id']) },
  delete_action_view_template: { label: 'Delete Action View',      icon: <Trash2 size={14}/>,  method: 'DELETE', url: p => `${ONTOLOGY_URL}/actions/_views/templates/${p.template_id}` },

  // ── Eval suites
  create_eval_suite:        { label: 'Create Eval Suite',        icon: <BarChart2 size={14}/>, method: 'POST',  url: () => `${EVAL_URL}/suites`,                                                          body: p => p },
  add_eval_case:            { label: 'Add Eval Case',            icon: <BarChart2 size={14}/>, method: 'POST',  url: p => `${EVAL_URL}/suites/${p.suite_id}/cases`,                                       body: p => omit(p, ['suite_id']) },
  run_eval_suite:           { label: 'Run Eval Suite',           icon: <Play size={14}/>,      method: 'POST',  url: p => `${EVAL_URL}/suites/${p.suite_id}/run`,                                         body: () => ({}) },

  // ── Alerts
  create_alert_rule:        { label: 'Create Alert Rule',        icon: <AlertTriangle size={14}/>, method: 'POST',   url: () => `${ALERT_URL}/alerts/rules`,                                            body: p => p },
  update_alert_rule:        { label: 'Update Alert Rule',        icon: <AlertTriangle size={14}/>, method: 'PATCH',  url: p => `${ALERT_URL}/alerts/rules/${p.rule_id}`,                                 body: p => omit(p, ['rule_id']) },
  delete_alert_rule:        { label: 'Delete Alert Rule',        icon: <Trash2 size={14}/>,        method: 'DELETE', url: p => `${ALERT_URL}/alerts/rules/${p.rule_id}` },
  test_alert_rule:          { label: 'Test Alert Rule',          icon: <Play size={14}/>,          method: 'POST',   url: p => `${ALERT_URL}/alerts/rules/${p.rule_id}/test`,                            body: () => ({}) },
  configure_notification_channel:{ label: 'Configure Channels',  icon: <Network size={14}/>,       method: 'PUT',    url: () => `${ALERT_URL}/alerts/channels`,                                          body: p => p },
  test_notification_channels:    { label: 'Test Channels',       icon: <Send size={14}/>,          method: 'POST',   url: () => `${ALERT_URL}/alerts/channels/test`,                                     body: () => ({}) },
  acknowledge_notification: { label: 'Acknowledge Notification', icon: <Check size={14}/>,        method: 'POST',    url: p => `${ALERT_URL}/alerts/notifications/${p.notification_id}/read`,           body: () => ({}) },
  snooze_notification:      { label: 'Snooze Notification',      icon: <Loader size={14}/>,       method: 'POST',    url: p => `${ALERT_URL}/alerts/notifications/${p.notification_id}/snooze`,         body: p => ({ until: p.until }) },

  // ── Approvals & checkpoints
  create_approval_workflow: { label: 'Create Approval Workflow', icon: <Check size={14}/>,   method: 'POST',   url: () => `${AUDIT_URL}/audit/approvals/workflows`,                                       body: p => p },
  update_approval_workflow: { label: 'Update Approval Workflow', icon: <Check size={14}/>,   method: 'PUT',    url: p => `${AUDIT_URL}/audit/approvals/workflows/${p.workflow_id}`,                       body: p => omit(p, ['workflow_id']) },
  delete_approval_workflow: { label: 'Delete Approval Workflow', icon: <Trash2 size={14}/>,  method: 'DELETE', url: p => `${AUDIT_URL}/audit/approvals/workflows/${p.workflow_id}` },
  approve_request:          { label: 'Approve Request',          icon: <Check size={14}/>,   method: 'POST',   url: p => `${AUDIT_URL}/audit/approvals/requests/${p.request_id}/approve`,                  body: p => ({ note: p.note || '' }) },
  reject_request:           { label: 'Reject Request',           icon: <XCircle size={14}/>, method: 'POST',   url: p => `${AUDIT_URL}/audit/approvals/requests/${p.request_id}/reject`,                   body: p => ({ reason: p.reason || '' }) },
  create_checkpoint:        { label: 'Create Checkpoint',        icon: <AlertTriangle size={14}/>, method: 'POST',   url: () => `${AUDIT_URL}/audit/checkpoints`,                                         body: p => p },
  update_checkpoint:        { label: 'Update Checkpoint',        icon: <AlertTriangle size={14}/>, method: 'PUT',    url: p => `${AUDIT_URL}/audit/checkpoints/${p.checkpoint_id}`,                       body: p => omit(p, ['checkpoint_id']) },
  delete_checkpoint:        { label: 'Delete Checkpoint',        icon: <Trash2 size={14}/>,        method: 'DELETE', url: p => `${AUDIT_URL}/audit/checkpoints/${p.checkpoint_id}` },

  // ── Users & tenants
  invite_user:              { label: 'Invite User',              icon: <Plus size={14}/>,    method: 'POST',   url: () => `${AUTH_URL}/auth/users`,                                                       body: p => p },
  update_user:              { label: 'Update User',              icon: <Plus size={14}/>,    method: 'PATCH',  url: p => `${AUTH_URL}/auth/users/${p.user_id}`,                                           body: p => omit(p, ['user_id']) },
  delete_user:              { label: 'Delete User',              icon: <Trash2 size={14}/>,  method: 'DELETE', url: p => `${AUTH_URL}/auth/users/${p.user_id}` },
  update_model_provider:    { label: 'Update Model Provider',    icon: <Zap size={14}/>,     method: 'PUT',    url: p => `${AGENT_URL}/model-providers/${p.provider_id}`,                                 body: p => omit(p, ['provider_id']) },
  delete_model_provider:    { label: 'Delete Model Provider',    icon: <Trash2 size={14}/>,  method: 'DELETE', url: p => `${AGENT_URL}/model-providers/${p.provider_id}` },
  test_model_provider:      { label: 'Test Model Provider',      icon: <Check size={14}/>,   method: 'POST',   url: p => `${AGENT_URL}/model-providers/${p.provider_id}/test`,                            body: () => ({}) },

  // ── Apps & shares
  update_app:               { label: 'Update App',               icon: <Layers size={14}/>,  method: 'PUT',    url: p => `${ONTOLOGY_URL}/apps/${p.app_id}`,                                              body: p => omit(p, ['app_id']) },
  delete_app:               { label: 'Delete App',               icon: <Trash2 size={14}/>,  method: 'DELETE', url: p => `${ONTOLOGY_URL}/apps/${p.app_id}` },
  create_app_share:         { label: 'Create App Share',         icon: <Network size={14}/>, method: 'POST',   url: p => `${ONTOLOGY_URL}/shares/apps/${p.app_id}/shares`,                                body: p => omit(p, ['app_id']) },
  revoke_app_share:         { label: 'Revoke App Share',         icon: <Trash2 size={14}/>,  method: 'DELETE', url: p => `${ONTOLOGY_URL}/shares/${p.share_id}` },

  // ── API gateway
  create_api_endpoint:      { label: 'Create API Endpoint',      icon: <Network size={14}/>, method: 'POST',   url: () => `${GATEWAY_URL}/gateway/manage`,                                               body: p => p },
  delete_api_endpoint:      { label: 'Delete API Endpoint',      icon: <Trash2 size={14}/>,  method: 'DELETE', url: p => `${GATEWAY_URL}/gateway/manage/${p.endpoint_id}` },
  mint_api_key:             { label: 'Mint API Key',             icon: <Zap size={14}/>,     method: 'POST',   url: () => `${GATEWAY_URL}/gateway/keys`,                                                 body: p => p },
  revoke_api_key:           { label: 'Revoke API Key',           icon: <Trash2 size={14}/>,  method: 'DELETE', url: p => `${GATEWAY_URL}/gateway/keys/${p.key_id}` },
  toggle_api_key:           { label: 'Toggle API Key',           icon: <Zap size={14}/>,     method: 'PATCH',  url: p => `${GATEWAY_URL}/gateway/keys/${p.key_id}/toggle`,                                body: p => ({ enabled: p.enabled }) },

  // ── Process mining
  discover_processes:       { label: 'Discover Processes',       icon: <Network size={14}/>, method: 'POST',   url: () => `${PROCESS_URL}/process/processes/auto-discover`,                              body: () => ({}) },
  backfill_process_case_key:{ label: 'Backfill Process Cases',   icon: <Loader size={14}/>,  method: 'POST',   url: p => `${PROCESS_URL}/process/processes/${p.process_id}/backfill`,                    body: () => ({}) },
  create_conformance_model: { label: 'Create Conformance Model', icon: <BarChart2 size={14}/>, method: 'POST', url: p => p.process_id ? `${PROCESS_URL}/process/conformance/models/by-process/${p.process_id}` : `${PROCESS_URL}/process/conformance/models/${p.object_type_id}`, body: p => omit(p, ['process_id', 'object_type_id']) },

  // ── PII & document extraction
  scan_pii_for_object_type: { label: 'Scan Object Type for PII', icon: <AlertTriangle size={14}/>, method: 'POST', url: () => `${INFERENCE_URL}/infer/scan-pii`,                                          body: p => ({ object_type_id: p.object_type_id }) },
  scan_all_pii:             { label: 'Scan All Object Types',    icon: <AlertTriangle size={14}/>, method: 'POST', url: () => `${INFERENCE_URL}/infer/scan-all`,                                          body: () => ({}) },
  extract_document_fields:  { label: 'Extract Document Fields',  icon: <Database size={14}/>,      method: 'POST', url: () => `${INFERENCE_URL}/infer/extract-from-document`,                              body: p => p },

  // ── Notebooks & comments
  create_notebook:          { label: 'Create Notebook',          icon: <Code size={14}/>,    method: 'POST',   url: () => `${ONTOLOGY_URL}/notebooks`,                                                    body: p => p },
  delete_notebook:          { label: 'Delete Notebook',          icon: <Trash2 size={14}/>,  method: 'DELETE', url: p => `${ONTOLOGY_URL}/notebooks/${p.notebook_id}` },
  add_comment:              { label: 'Add Comment',              icon: <MessageSquare size={14}/>, method: 'POST',   url: () => `${COLLAB_URL}/comments`,                                                  body: p => p },
  resolve_comment:          { label: 'Resolve Comment',          icon: <Check size={14}/>,         method: 'PATCH',  url: p => `${COLLAB_URL}/comments/${p.comment_id}`,                                  body: () => ({ resolved: true }) },
};

const ALL_ACTION_TYPES: NexusActionType[] = [
  'create_connector', 'create_object_type', 'create_pipeline', 'create_logic',
  'run_pipeline', 'create_ontology_link', 'ingest_records', 'create_app',
  'create_app_action', 'register_model_provider',
  ...Object.keys(GENERIC_ACTION_REGISTRY),
];

const ACTION_LABELS: Record<string, string> = {
  create_connector: 'Create Connector',
  create_object_type: 'Create Object Type',
  create_pipeline: 'Create Pipeline',
  create_logic: 'Create Logic Function',
  run_pipeline: 'Run Pipeline',
  create_ontology_link: 'Create Ontology Link',
  ingest_records: 'Ingest Records',
  create_app: 'Create App / Dashboard',
  create_app_action: 'Add Action to App',
  register_model_provider: 'Register AI Model',
  ...Object.fromEntries(Object.entries(GENERIC_ACTION_REGISTRY).map(([k, v]) => [k, v.label])),
};

const ACTION_ICONS: Record<string, React.ReactNode> = {
  create_connector: <Database size={14} />,
  create_object_type: <Layers size={14} />,
  create_pipeline: <GitBranch size={14} />,
  create_logic: <Code size={14} />,
  run_pipeline: <Play size={14} />,
  create_ontology_link: <GitBranch size={14} />,
  ingest_records: <Database size={14} />,
  create_app: <Layers size={14} />,
  create_app_action: <Code size={14} />,
  register_model_provider: <Zap size={14} />,
  ...Object.fromEntries(Object.entries(GENERIC_ACTION_REGISTRY).map(([k, v]) => [k, v.icon])),
};

function ActionConfirmCard({
  action,
  onConfirm,
  onReject,
  status,
}: {
  action: NexusAction;
  onConfirm: () => void;
  onReject: () => void;
  status: 'pending' | 'executing' | 'done' | 'error' | 'rejected';
}) {
  const { t } = useTranslation(['shell', 'common']);
  const label = ACTION_LABELS[action.type] || action.type;
  const icon = ACTION_ICONS[action.type] || <Layers size={14} />;

  return (
    <div style={{
      border: '1px solid var(--color-interactive-border)',
      borderRadius: 8,
      backgroundColor: 'var(--color-interactive-dim)',
      padding: '12px 14px',
      margin: '8px 0',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <div style={{
          width: 28, height: 28, borderRadius: 6,
          backgroundColor: 'var(--color-interactive)', color: '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>{icon}</div>
        <div>
          <div style={{ fontSize: 12, fontWeight: 600, color: mix.textStrong }}>{label}</div>
          <div style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>{action.name}</div>
        </div>
      </div>

      <div style={{ marginBottom: 10 }}>
        {action.summary.map((s, i) => (
          <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 2, fontSize: 12, color: mix.textSecondary }}>
            <span style={{ color: 'var(--color-interactive)', flexShrink: 0 }}>•</span>
            <span>{s}</span>
          </div>
        ))}
      </div>

      {status === 'pending' && (
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={onConfirm}
            style={{
              display: 'flex', alignItems: 'center', gap: 5,
              padding: '6px 14px', fontSize: 12, fontWeight: 500,
              backgroundColor: 'var(--color-interactive)', color: '#fff',
              border: 'none', borderRadius: 4, cursor: 'pointer',
            }}
          >
            <Check size={13} /> {t('shell.assistant.actionCard.confirm', 'Confirmar')}
          </button>
          <button
            onClick={onReject}
            style={{
              display: 'flex', alignItems: 'center', gap: 5,
              padding: '6px 14px', fontSize: 12, fontWeight: 500,
              backgroundColor: 'var(--color-surface)', color: 'var(--color-text-muted)',
              border: '1px solid var(--color-border)', borderRadius: 4, cursor: 'pointer',
            }}
          >
            <XCircle size={13} /> {t('shell.assistant.actionCard.reject', 'Rechazar')}
          </button>
        </div>
      )}
      {status === 'executing' && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--color-interactive)' }}>
          <Loader size={13} style={{ animation: 'spin 0.7s linear infinite' }} /> {t('shell.assistant.actionCard.executing', 'Ejecutando…')}
        </div>
      )}
      {status === 'done' && (
        <div style={{ fontSize: 12, color: 'var(--color-status-green)', fontWeight: 500 }}>
          <Check size={13} style={{ verticalAlign: 'middle', marginRight: 4 }} />
          {t('shell.assistant.actionCard.done', 'Completado con éxito')}
        </div>
      )}
      {status === 'error' && (
        <div style={{ fontSize: 12, color: 'var(--color-status-red)', fontWeight: 500 }}>
          {t('shell.assistant.actionCard.failed', 'Fallido — revisa los registros')}
        </div>
      )}
      {status === 'rejected' && (
        <div style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>{t('shell.assistant.actionCard.rejected', 'Rechazado')}</div>
      )}
    </div>
  );
}

// ── Markdown-lite renderer ────────────────────────────────────────────────────
function Markdown({ text }: { text: string }) {
  const lines = text.split('\n');
  const elements: React.ReactNode[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];
    if (line.startsWith('### ')) {
      elements.push(<div key={i} style={{ fontSize: 12, fontWeight: 700, color: 'var(--color-text)', marginTop: 10, marginBottom: 2 }}>{line.slice(4)}</div>);
    } else if (line.startsWith('## ')) {
      elements.push(<div key={i} style={{ fontSize: 13, fontWeight: 700, color: 'var(--color-text)', marginTop: 12, marginBottom: 3 }}>{line.slice(3)}</div>);
    } else if (line.startsWith('# ')) {
      elements.push(<div key={i} style={{ fontSize: 14, fontWeight: 700, color: 'var(--color-text)', marginTop: 12, marginBottom: 4 }}>{line.slice(2)}</div>);
    } else if (/^\d+[.)] /.test(line)) {
      const m = /^(\d+)[.)] (.*)$/.exec(line)!;
      elements.push(
        <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 2 }}>
          <span style={{ color: 'var(--color-brand)', flexShrink: 0, fontWeight: 600, minWidth: 14 }}>{m[1]}.</span>
          <span>{inlineFormat(m[2])}</span>
        </div>
      );
    } else if (line.startsWith('- ') || line.startsWith('* ')) {
      elements.push(
        <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 2 }}>
          <span style={{ color: 'var(--color-brand)', flexShrink: 0, marginTop: 1 }}>•</span>
          <span>{inlineFormat(line.slice(2))}</span>
        </div>
      );
    } else if (line.startsWith('```')) {
      const lang = line.slice(3).trim();
      const codeLines: string[] = [];
      i++;
      while (i < lines.length && !lines[i].startsWith('```')) {
        codeLines.push(lines[i]);
        i++;
      }
      // Hide action blocks (any format) — parent renders them as ActionConfirmCard
      const codeText = codeLines.join('\n');
      // Guide blocks render as AssistantGuideCard under the message.
      let isAction = lang === 'nexus-action' || lang === GUIDE_LANG;
      if (!isAction) {
        try {
          const obj = JSON.parse(codeText);
          if (obj?.type && obj?.summary && ALL_ACTION_TYPES.includes(obj.type)) {
            isAction = true;
          }
        } catch {
          // Try to find action JSON even if there's extra whitespace/content
          if (ALL_ACTION_TYPES.some((t) => codeText.includes(`"${t}"`))) {
            const found = extractAction(codeText);
            if (found) isAction = true;
          }
        }
      }
      if (!isAction) {
        elements.push(
          <pre key={i} style={{
            backgroundColor: mix.fillSoft, border: '1px solid var(--color-border)',
            padding: '8px 10px', fontSize: 11, fontFamily: 'monospace',
            overflowX: 'auto', margin: '6px 0', whiteSpace: 'pre-wrap', wordBreak: 'break-all',
          }}>
            {codeLines.join('\n')}
          </pre>
        );
      }
    } else if (line === '') {
      if (i > 0 && lines[i - 1] !== '') elements.push(<div key={i} style={{ height: 6 }} />);
    } else if (line.trimStart().startsWith('{"type"') && ALL_ACTION_TYPES.some((t) => line.includes(`"${t}"`))) {
      // Bare action JSON line — skip rendering, handled by ActionConfirmCard
    } else {
      elements.push(<div key={i} style={{ marginBottom: 2 }}>{inlineFormat(line)}</div>);
    }
    i++;
  }
  return <>{elements}</>;
}

/** Extract a nexus-action JSON from a message, if present */
function extractAction(text: string): NexusAction | null {
  // Find the start of an action-shaped JSON anywhere in the text
  const needle = '"type"';
  const actionTypes = ALL_ACTION_TYPES;
  let searchFrom = 0;
  while (searchFrom < text.length) {
    const idx = text.indexOf(needle, searchFrom);
    if (idx === -1) break;
    // Walk backwards to find the opening {
    let braceStart = -1;
    for (let j = idx - 1; j >= 0; j--) {
      if (text[j] === '{') { braceStart = j; break; }
      if (text[j] !== ' ' && text[j] !== '"' && text[j] !== '\n' && text[j] !== '`') break;
    }
    if (braceStart === -1) { searchFrom = idx + 1; continue; }
    // Walk forward with bracket counting to find the matching }
    let depth = 0;
    let braceEnd = -1;
    let inString = false;
    let escaped = false;
    for (let j = braceStart; j < text.length; j++) {
      const ch = text[j];
      if (escaped) { escaped = false; continue; }
      if (ch === '\\' && inString) { escaped = true; continue; }
      if (ch === '"') { inString = !inString; continue; }
      if (inString) continue;
      if (ch === '{') depth++;
      if (ch === '}') { depth--; if (depth === 0) { braceEnd = j; break; } }
    }
    if (braceEnd === -1) { searchFrom = idx + 1; continue; }
    const candidate = text.slice(braceStart, braceEnd + 1);
    try {
      const obj = JSON.parse(candidate);
      if (obj?.type && obj?.summary && actionTypes.includes(obj.type)) {
        return obj as NexusAction;
      }
    } catch { /* not valid JSON */ }
    searchFrom = idx + 1;
  }
  return null;
}

function inlineFormat(text: string): React.ReactNode {
  const parts = text.split(/(`[^`]+`|\*\*[^*]+\*\*|\*[^*]+\*)/g);
  return parts.map((p, i) => {
    if (p.startsWith('`') && p.endsWith('`'))
      return <code key={i} style={{ backgroundColor: 'var(--color-brand-dim)', color: 'var(--color-brand)', padding: '1px 5px', borderRadius: 3, fontSize: 11 }}>{p.slice(1, -1)}</code>;
    if (p.startsWith('**') && p.endsWith('**'))
      return <strong key={i}>{p.slice(2, -2)}</strong>;
    if (p.startsWith('*') && p.endsWith('*'))
      return <em key={i}>{p.slice(1, -1)}</em>;
    return p;
  });
}

// ── Action cards ──────────────────────────────────────────────────────────────
interface ActionDef {
  title: string;
  description: string;
  prefill: string;
  Icon: LucideIcon;
}

function getAssistantActions(t: any): ActionDef[] {
  return [
    {
      title: t('shell.assistant.actionsPanel.createPipeline.title', 'Crear Pipeline'),
      Icon: GitBranch,
      description: t('shell.assistant.actionsPanel.createPipeline.desc', 'Describe un pipeline en lenguaje cotidiano'),
      prefill: t('shell.assistant.actionsPanel.createPipeline.prefill', 'Crea un pipeline que '),
    },
    {
      title: t('shell.assistant.actionsPanel.createLogic.title', 'Crear Función Lógica'),
      Icon: Code,
      description: t('shell.assistant.actionsPanel.createLogic.desc', 'Describe la lógica de negocio a generar'),
      prefill: t('shell.assistant.actionsPanel.createLogic.prefill', 'Crea una función lógica que '),
    },
    {
      title: t('shell.assistant.actionsPanel.explainLineage.title', 'Explicar Linaje'),
      Icon: Network,
      description: t('shell.assistant.actionsPanel.explainLineage.desc', 'Explica el flujo de datos para un tipo de registro'),
      prefill: t('shell.assistant.actionsPanel.explainLineage.prefill', 'Explica el linaje de '),
    },
    {
      title: t('shell.assistant.actionsPanel.surfaceAnomalies.title', 'Detectar Anomalías'),
      Icon: AlertTriangle,
      description: t('shell.assistant.actionsPanel.surfaceAnomalies.desc', 'Encuentra problemas de calidad de datos'),
      prefill: t('shell.assistant.actionsPanel.surfaceAnomalies.prefill', 'Detecta anomalías en '),
    },
    {
      title: t('shell.assistant.actionsPanel.runEvaluation.title', 'Ejecutar Evaluación'),
      Icon: BarChart2,
      description: t('shell.assistant.actionsPanel.runEvaluation.desc', "Evalúa la precisión de un pipeline"),
      prefill: t('shell.assistant.actionsPanel.runEvaluation.prefill', 'Ejecuta una evaluación en '),
    },
  ];
}

interface ActionCardProps extends ActionDef {
  onSelect: (prefill: string) => void;
}

function ActionCard({ title, description, prefill, Icon, onSelect }: ActionCardProps) {
  return (
    <div
      onClick={() => onSelect(prefill)}
      style={{
        border: '1px solid var(--color-border)',
        borderRadius: 6,
        padding: '10px 12px',
        cursor: 'pointer',
        backgroundColor: 'var(--color-base)',
        transition: 'all 80ms',
      }}
      onMouseEnter={e => {
        e.currentTarget.style.backgroundColor = 'var(--color-interactive-dim)';
        e.currentTarget.style.borderColor = 'color-mix(in srgb, #93C5FD, var(--color-border) var(--tint-sink))';
      }}
      onMouseLeave={e => {
        e.currentTarget.style.backgroundColor = 'var(--color-base)';
        e.currentTarget.style.borderColor = 'var(--color-border)';
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        <Icon size={14} style={{ color: 'var(--color-interactive)' }} />
        <span style={{ fontSize: 12, fontWeight: 600, color: mix.textStrong }}>{title}</span>
      </div>
      <p style={{ fontSize: 11, color: 'var(--color-text-muted)', margin: 0 }}>{description}</p>
    </div>
  );
}

// ── Main panel ────────────────────────────────────────────────────────────────
// `embedded` (ui_v2, C18): drawn as the General mode of the single assistant
// panel (AssistantPanel), which owns the frame, the close button and the
// docking. Without it, the overlay panel of before, unchanged.
const NexusAssistant: React.FC<{ embedded?: boolean }> = ({ embedded = false }) => {
  const { t } = useTranslation(['shell', 'common']);
  const { currentPage } = useNavigationStore();
  const {
    open, setOpen, activeId,
    newConversation, selectConversation, deleteConversation, addMessage,
    updateMessageContent, updateMessageStreaming, setMessageFeedback, setStreamingMessageId,
  } = useAssistantStore();
  const conversations = useTenantConversations();
  const { objectTypes, fetchObjectTypes } = useOntologyStore();

  const [view, setView]     = useState<'list' | 'chat'>('list');
  const [input, setInput]   = useState('');
  const [loading, setLoading] = useState(false);
  const [chatMode, setChatMode] = useState<'help' | 'data' | 'actions'>('help');
  const [selectedObjectTypeId, setSelectedObjectTypeId] = useState<string>('');
  // Data mode: with a project open, offer its object types (the chosen one stays).
  const pickableObjectTypes = useProjectObjectTypes(objectTypes, [selectedObjectTypeId]);
  const [actionStatuses, setActionStatuses] = useState<Record<string, 'pending' | 'executing' | 'done' | 'error' | 'rejected'>>({});
  const bottomRef = useRef<HTMLDivElement>(null);
  const inputRef  = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (open && chatMode === 'data' && objectTypes.length === 0) {
      fetchObjectTypes();
    }
  }, [open, chatMode]);

  const activeConvo = conversations.find(c => c.id === activeId) ?? null;

  useEffect(() => {
    if (activeId) setView('chat');
  }, [activeId]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [activeConvo?.messages.length, loading]);

  useEffect(() => {
    if (view === 'chat' && open) setTimeout(() => inputRef.current?.focus(), 120);
  }, [view, open]);

  const openChat = (id: string) => { selectConversation(id); setView('chat'); };

  const startNew = () => { newConversation(); setView('chat'); };

  const executeAction = async (msgId: string, action: NexusAction) => {
    setActionStatuses(s => ({ ...s, [msgId]: 'executing' }));
    const tenantId = getTenantId();
    const headers: Record<string, string> = { 'Content-Type': 'application/json', 'x-tenant-id': tenantId };

    try {
      let url = '';
      let body: Record<string, unknown> = {};

      if (action.type === 'create_connector') {
        url = `${CONNECTOR_URL}/connectors`;
        body = {
          name: action.payload.name,
          type: action.payload.type || 'REST_API',
          category: action.payload.category || 'API',
          description: action.payload.description || '',
          base_url: action.payload.base_url || '',
          auth_type: action.payload.auth_type || 'None',
          credentials: action.payload.credentials || null,
          headers: action.payload.headers || null,
          config: action.payload.endpoints ? { endpoints: action.payload.endpoints } : action.payload.config || null,
        };
      } else if (action.type === 'create_object_type') {
        url = `${ONTOLOGY_URL}/object-types`;
        const toDisplayName = (s: string) => s.replace(/[_-]/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
        const typeToSemantic: Record<string, string> = {
          string: 'TEXT', text: 'TEXT', number: 'QUANTITY', integer: 'QUANTITY',
          float: 'QUANTITY', boolean: 'BOOLEAN', date: 'DATE', datetime: 'DATETIME',
          url: 'URL', email: 'EMAIL', phone: 'PHONE', currency: 'CURRENCY',
          percentage: 'PERCENTAGE', id: 'IDENTIFIER', identifier: 'IDENTIFIER',
        };
        const props = ((action.payload.properties || []) as Record<string, unknown>[]).map(p => ({
          name: p.name,
          display_name: p.display_name || toDisplayName(String(p.name || '')),
          semantic_type: p.semantic_type || typeToSemantic[String(p.type || 'string').toLowerCase()] || 'TEXT',
          data_type: p.data_type || String(p.type || 'string'),
          pii_level: p.pii_level || 'NONE',
          required: p.required ?? false,
          description: p.description || '',
        }));
        body = {
          name: action.payload.name,
          display_name: action.payload.display_name || toDisplayName(String(action.payload.name || '')),
          description: action.payload.description || '',
          properties: props,
          tenant_id: tenantId,
          source_connector_ids: [],
        };
      } else if (action.type === 'create_pipeline') {
        url = `${INFERENCE_URL}/infer/create-pipeline`;
        body = action.payload;
      } else if (action.type === 'create_logic') {
        // Redirect into the Logic Studio Copilot instead of the old silent
        // one-shot generation: create a draft function, queue the idea for
        // the Copilot (which builds it as staged proposals the user approves
        // block by block, validates and dry-runs it), and navigate there.
        const idea = String(action.payload.description || action.name || '').trim();
        if (!idea) throw new Error('create_logic needs a description of the function to build.');
        const fn = await useLogicStore.getState().createFunction({
          name: i18n.t('logic:untitledFunction', 'Función sin nombre'), blocks: [], input_schema: [],
        });
        useLogicStore.getState().setPendingCopilotSeed({ prompt: idea });
        useNavigationStore.getState().navigateTo('logic');
        setActionStatuses(s => ({ ...s, [msgId]: 'done' }));
        if (activeId) {
          addMessage(activeId, {
            id: uuid(),
            role: 'assistant',
            content: `Opening **Logic Studio** — the Copilot is building this with you (function \`${fn.id.slice(0, 8)}\`). Every block it proposes needs your approval before it becomes part of the function.`,
            timestamp: new Date().toISOString(),
          });
        }
        return;
      } else if (action.type === 'run_pipeline') {
        const pid = action.payload.pipeline_id as string;
        url = `${PIPELINE_URL}/pipelines/${pid}/run`;
        body = {};
      } else if (action.type === 'create_ontology_link') {
        url = `${ONTOLOGY_URL}/object-types/links`;
        const p = action.payload;
        // `source_field: ''` era el default y garantizaba un link roto: el
        // traversal lee `data.get('')` y siempre da None. El backend ahora lo
        // rechaza con 400, asi que se manda el par solo si hay un campo real.
        const srcField = String(p.source_field || '').trim();
        const tgtField = String(p.target_field || 'id').trim();
        body = {
          source_object_type_id: p.source_object_type_id,
          target_object_type_id: p.target_object_type_id,
          relationship_type: p.relationship_type || 'belongs_to',
          join_keys: (srcField && tgtField)
            ? [{ source_field: srcField, target_field: tgtField }]
            : [],
          label: p.label || '',
          is_inferred: false,
          // Many-to-many through a join table: {object_type_id, source_key, target_key}.
          through: p.through && typeof p.through === 'object' ? p.through : null,
          normalize_keys: p.normalize_keys === true,
        };
      } else if (action.type === 'ingest_records') {
        const otId = action.payload.object_type_id as string;
        url = `${ONTOLOGY_URL}/object-types/${otId}/records/ingest`;
        body = {
          records: action.payload.records || [],
          pk_field: action.payload.pk_field || 'id',
          pipeline_id: 'assistant-ingest',
        };
      } else if (action.type === 'create_app') {
        url = `${ONTOLOGY_URL}/apps`;
        const p = action.payload;
        // Fall back to action.name when the LLM puts it at the top level
        // instead of in payload — common mistake since the outer card label
        // and the app name are usually the same string.
        const appName = (p.name as string) || action.name;
        if (!appName) {
          throw new Error('App name is required (set "name" inside the payload).');
        }
        body = {
          name: appName,
          description: p.description || '',
          icon: p.icon || '',
          object_type_ids: p.object_type_ids || [],
          components: p.components || [],
          settings: p.settings || {},
          kind: p.kind || 'dashboard',
        };
      } else if (action.type === 'register_model_provider') {
        url = `${AGENT_URL}/model-providers`;
        const p = action.payload;
        body = {
          name: p.name,
          provider_type: p.provider_type || 'openai',
          api_key_encrypted: p.api_key || p.api_key_encrypted || null,
          base_url: p.base_url || null,
          models: p.models || [],
          is_default: p.is_default ?? false,
          enabled: p.enabled ?? true,
        };
      } else if (action.type === 'create_app_action') {
        // PUT /apps/{id} merging the new action into settings.actions[].
        const appId = action.payload.app_id as string;
        const newAction = action.payload.action as Record<string, unknown>;
        // Fetch the current app, append, PUT.
        const cur = await fetch(`${ONTOLOGY_URL}/apps/${appId}`, { headers });
        if (!cur.ok) throw new Error(`Could not fetch app ${appId}: ${cur.status}`);
        const app = await cur.json();
        const settings = (app.settings as Record<string, unknown>) || {};
        const actions = ((settings.actions as Record<string, unknown>[]) || []).filter(
          (a) => a.id !== newAction.id,
        );
        const nextSettings = { ...settings, actions: [...actions, newAction] };
        const putResp = await fetch(`${ONTOLOGY_URL}/apps/${appId}`, {
          method: 'PUT', headers, body: JSON.stringify({ settings: nextSettings }),
        });
        if (!putResp.ok) {
          throw new Error(`${putResp.status}: ${(await putResp.text()).slice(0, 200)}`);
        }
        setActionStatuses(s => ({ ...s, [msgId]: 'done' }));
        if (activeId) {
          addMessage(activeId, {
            id: uuid(),
            role: 'assistant',
            content: `Added action **${(newAction.name as string) || newAction.id}** to app \`${appId}\`.`,
            timestamp: new Date().toISOString(),
          });
        }
        return;
      } else if (GENERIC_ACTION_REGISTRY[action.type]) {
        // ── Generic / table-driven dispatcher ──────────────────────────────
        // Every action registered in GENERIC_ACTION_REGISTRY is dispatched
        // here. Adding a new action is one row in that table, no code changes.
        const exec = GENERIC_ACTION_REGISTRY[action.type];
        const targetUrl = exec.url(action.payload);
        const targetBody = exec.body ? exec.body(action.payload) : null;
        const init: RequestInit = { method: exec.method, headers };
        if (targetBody !== null && exec.method !== 'DELETE') {
          init.body = JSON.stringify(targetBody);
        }
        const resp = await fetch(targetUrl, init);
        if (!resp.ok) {
          const errText = await resp.text();
          throw new Error(`${resp.status}: ${errText.slice(0, 200)}`);
        }
        // DELETE typically returns 204 No Content
        let data: any = null;
        if (resp.status !== 204) {
          try { data = await resp.json(); } catch { /* empty body */ }
        }
        setActionStatuses(s => ({ ...s, [msgId]: 'done' }));
        if (activeId) {
          const summary = data && typeof data === 'object'
            ? (data.id ? ` (id: \`${data.id}\`)` : '')
            : '';
          addMessage(activeId, {
            id: uuid(),
            role: 'assistant',
            content: `**${action.name || ACTION_LABELS[action.type]}** completed${summary}.`,
            timestamp: new Date().toISOString(),
          });
        }
        return;
      }

      const resp = await fetch(url, { method: 'POST', headers, body: JSON.stringify(body) });
      if (!resp.ok) {
        const errText = await resp.text();
        throw new Error(`${resp.status}: ${errText.slice(0, 200)}`);
      }
      const data = await resp.json();

      // Check if the backend reports failure (created: false)
      if (data.created === false) {
        setActionStatuses(s => ({ ...s, [msgId]: 'error' }));
        if (activeId) {
          addMessage(activeId, {
            id: uuid(),
            role: 'assistant',
            content: `Failed to create **${action.name}**: ${data.message || 'Unknown error'}`,
            timestamp: new Date().toISOString(),
          });
        }
        return;
      }

      setActionStatuses(s => ({ ...s, [msgId]: 'done' }));

      // Add a result message
      if (activeId) {
        const idStr = data.id ? ` ID: \`${data.id}\`` : data.pipeline_id ? ` ID: \`${data.pipeline_id}\`` : data.function_id ? ` ID: \`${data.function_id}\`` : '';
        const resultText = action.type === 'run_pipeline'
          ? `Pipeline run started.`
          : `**${action.name}** created successfully.${idStr}`;
        addMessage(activeId, {
          id: uuid(),
          role: 'assistant',
          content: resultText,
          timestamp: new Date().toISOString(),
        });
      }
    } catch (err) {
      setActionStatuses(s => ({ ...s, [msgId]: 'error' }));
      if (activeId) {
        addMessage(activeId, {
          id: uuid(),
          role: 'assistant',
          content: `Action failed: ${err instanceof Error ? err.message : 'Unknown error'}`,
          timestamp: new Date().toISOString(),
        });
      }
    }
  };

  const rejectAction = (msgId: string) => {
    setActionStatuses(s => ({ ...s, [msgId]: 'rejected' }));
    if (activeId) {
      addMessage(activeId, {
        id: uuid(),
        role: 'assistant',
        content: 'Action cancelled. Let me know if you want to adjust the plan.',
        timestamp: new Date().toISOString(),
      });
    }
  };

  const send = async () => {
    const text = input.trim();
    if (!text || loading || !activeId) return;
    setInput('');

    const now = new Date().toISOString();
    const userMsg: AssistantMessage = {
      id: uuid(),
      role: 'user',
      content: text,
      timestamp: now,
    };
    addMessage(activeId, userMsg);
    setLoading(true);

    const convo = conversations.find(c => c.id === activeId);
    const history = [...(convo?.messages ?? []), userMsg];

    try {
      if (chatMode === 'data' && selectedObjectTypeId) {
        const ot = objectTypes.find(o => o.id === selectedObjectTypeId);
        let records: unknown[] = [];
        try {
          const r = await fetch(
            `${ONTOLOGY_URL}/object-types/${selectedObjectTypeId}/records?limit=100`,
            { headers: { 'x-tenant-id': getTenantId() } },
          );
          if (r.ok) { const d = await r.json(); records = d.records || []; }
        } catch { /* ignore */ }

        const res = await fetch(`${INFERENCE_URL}/infer/chat`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'x-tenant-id': getTenantId() },
          body: JSON.stringify({
            question: text,
            object_type_id: selectedObjectTypeId,
            object_type_name: ot?.displayName || ot?.name || selectedObjectTypeId,
            fields: (ot?.properties || []).map((p: { name: string }) => p.name),
            records: records.slice(0, 50),
          }),
        });
        // Same guard as the streaming branch: without it a 429 fell through to
        // `data.answer` being undefined and rendered a bare "No response."
        if (isAiLimited(res)) {
          const info = await parseAiLimit(res);
          addMessage(activeId, {
            id: uuid(),
            role: 'assistant',
            kind: 'limit',
            title: aiLimitTitle(info),
            content: aiLimitMessage(info),
            timestamp: new Date().toISOString(),
          });
          return;
        }
        if (!res.ok) {
          addMessage(activeId, {
            id: uuid(),
            role: 'assistant',
            content: await assistantErrorText(res),
            timestamp: new Date().toISOString(),
          });
          return;
        }
        const data = await res.json();
        addMessage(activeId, {
          id: uuid(),
          role: 'assistant',
          content: data.answer || 'No response.',
          timestamp: new Date().toISOString(),
        });
      } else {
        // Streaming platform help mode
        const context: Record<string, unknown> = await fetchLiveContext(currentPage).catch(() => ({ current_page: currentPage }));
        // Conversations started from a widget correlation analysis carry the
        // deterministic stats; follow-ups must cite those exact numbers.
        if (convo?.analysisContext) context.widget_analysis = convo.analysisContext;
        const response = await fetch(`${INFERENCE_URL}/infer/stream-help`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'x-tenant-id': getTenantId() },
          body: JSON.stringify({ messages: history, context }),
        });

        // Check the status BEFORE opening a reader. A 429 body is JSON, not SSE,
        // so the loop below finds no `data:` lines and leaves the placeholder
        // message empty — the user saw a blank bubble with no explanation.
        if (isAiLimited(response)) {
          const info = await parseAiLimit(response);
          addMessage(activeId, {
            id: uuid(),
            role: 'assistant',
            kind: 'limit',
            title: aiLimitTitle(info),
            content: aiLimitMessage(info),
            timestamp: new Date().toISOString(),
          });
          return;
        }

        // Any other non-2xx has the same shape problem: a JSON error body read as
        // SSE yields an empty bubble. Surface it instead of swallowing it.
        if (!response.ok || !response.body) {
          addMessage(activeId, {
            id: uuid(),
            role: 'assistant',
            content: await assistantErrorText(response),
            timestamp: new Date().toISOString(),
          });
          return;
        }

        const assistantMsgId = uuid();
        addMessage(activeId, {
          id: assistantMsgId,
          role: 'assistant',
          content: '',
          streaming: true,
          timestamp: new Date().toISOString(),
        });
        setStreamingMessageId(assistantMsgId);

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let accumulated = '';

        outer: while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          const chunk = decoder.decode(value);
          const lines = chunk.split('\n');
          for (const line of lines) {
            if (line.startsWith('data: ')) {
              const data = line.slice(6).trim();
              if (data === '[DONE]') break outer;
              try {
                const parsed = JSON.parse(data);
                if (parsed.text) {
                  accumulated += parsed.text;
                  updateMessageContent(assistantMsgId, accumulated);
                }
                if (parsed.error) {
                  updateMessageContent(assistantMsgId, `Error: ${parsed.error}`);
                }
              } catch { /* ignore malformed SSE lines */ }
            }
          }
        }

        setStreamingMessageId(null);
        updateMessageStreaming(assistantMsgId, false);
      }
    } catch {
      addMessage(activeId, {
        id: uuid(),
        role: 'assistant',
        content: "Couldn't reach the inference service.",
        timestamp: new Date().toISOString(),
      });
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  const C0 = {
    bg: 'var(--color-base)', sidebar: '#F1F5F9', panel: 'var(--color-surface)',
    border: 'var(--color-border)', accent: 'var(--color-brand)', accentDim: 'var(--color-brand-dim)',
    text: 'var(--color-text)', muted: 'var(--color-text-muted)', dim: 'var(--color-text-subtle)',
    dark: '#080E18', darkBorder: '#131C2E',
  };
  // In the single panel the controls take the interactive blue and the theme
  // tints; violet stays on what marks the AI (its avatar), as 1.3 asks.
  const C = embedded
    ? { ...C0, sidebar: 'var(--color-base)', accent: 'var(--color-interactive)', accentDim: 'var(--color-interactive-dim)' }
    : C0;

  return (
    <>
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes blink { 0%,100%{opacity:1} 50%{opacity:0} }
      `}</style>

      {/* Backdrop */}
      {!embedded && (
      <div
        onClick={() => setOpen(false)}
        style={{ position: 'fixed', inset: 0, zIndex: 149, backgroundColor: 'rgba(0,0,0,0.15)' }}
      />
      )}

      {/* Panel */}
      <div data-testid={embedded ? 'assistant-general' : undefined} data-tour-skip style={embedded ? {
        flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', backgroundColor: C.panel,
      } : {
        position: 'fixed', top: 0, right: 0, bottom: 0, width: 420,
        backgroundColor: C.panel, borderLeft: `1px solid ${C.border}`,
        boxShadow: '-4px 0 24px rgba(0,0,0,0.12)',
        display: 'flex', flexDirection: 'column',
        zIndex: 150, fontFamily: 'system-ui, sans-serif',
      }}>

        {/* Header — in the single panel, a slim bar under its tabs: back,
            conversation title and "Nuevo chat"; the panel has the close. */}
        {embedded ? (
          <div className="as-subbar">
            {view === 'chat' && (
              <button type="button" className="as-icon-btn" onClick={() => setView('list')}
                title={t('shell.assistant.v2.conversations', 'Conversaciones')} aria-label={t('shell.assistant.v2.conversations', 'Conversaciones')}>
                <ArrowLeft size={14} />
              </button>
            )}
            <span className="as-subbar__title">
              {view === 'chat' && activeConvo
                ? (activeConvo.title === 'New conversation' ? t('shell.assistant.newConversation', 'Nueva conversación') : activeConvo.title)
                : t('shell.assistant.v2.conversations', 'Conversaciones')}
            </span>
            <button type="button" className="ds-btn ds-btn--ghost ds-btn--sm" onClick={startNew}>
              <Plus size={13} /> {t('shell.assistant.newChat', 'Nuevo chat')}
            </button>
          </div>
        ) : (
        <div style={{
          height: 52, display: 'flex', alignItems: 'center', gap: 10,
          padding: '0 16px', borderBottom: `1px solid ${C.border}`,
          backgroundColor: C.dark, flexShrink: 0,
        }}>
          {view === 'chat' && (
            <button onClick={() => setView('list')} style={{ background: 'none', border: 'none', color: 'var(--color-text-muted)', cursor: 'pointer', padding: 4, lineHeight: 0 }}>
              <ArrowLeft size={16} />
            </button>
          )}
          <span style={{ color: '#F8FAFC', fontSize: 14, fontWeight: 600, flex: 1 }}>
            {view === 'chat' && activeConvo
              ? (activeConvo.title === 'New conversation' ? t('shell.assistant.newConversation', 'Nueva conversación') : activeConvo.title)
              : t('shell.assistant.title', 'Asistente de Nexus')}
          </span>
          <button
            onClick={startNew}
            style={{
              display: 'flex', alignItems: 'center', gap: 5,
              padding: '5px 10px', fontSize: 12, cursor: 'pointer',
              backgroundColor: C.accentDim, color: C.accent,
              border: `1px solid ${withAlpha(C.accent, 0.27)}`,
            }}
          >
            <Plus size={12} /> {t('shell.assistant.newChat', 'Nuevo chat')}
          </button>
          <button onClick={() => setOpen(false)} style={{ background: 'none', border: 'none', color: mix.textTertiary, cursor: 'pointer', padding: 4, lineHeight: 0 }}>
            <X size={16} />
          </button>
        </div>
        )}

        {/* ── Conversation list ── */}
        {view === 'list' && (
          <div style={{ flex: 1, overflowY: 'auto' }}>
            {conversations.length === 0 ? (
              <div style={{ padding: 32, textAlign: 'center', color: C.dim }}>
                <MessageSquare size={32} color={C.border} style={{ marginBottom: 12 }} />
                <div style={{ fontSize: 13, marginBottom: 6 }}>{t('shell.assistant.noConversations', 'Aún no hay conversaciones')}</div>
                <div style={{ fontSize: 12, color: C.dim, marginBottom: 16 }}>{t('shell.assistant.askAnything', 'Pregunta lo que sea sobre tu configuración de Nexus')}</div>
                <button onClick={startNew} style={{
                  padding: '8px 18px', backgroundColor: C.accentDim, color: C.accent,
                  border: `1px solid ${C.accent}`, cursor: 'pointer', fontSize: 13,
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                }}>
                  <Plus size={13} /> {t('shell.assistant.startConversation', 'Iniciar una conversación')}
                </button>
              </div>
            ) : (
              <div style={{ padding: '6px 0' }}>
                {/* Quick suggestions */}
                <div style={{ padding: '10px 16px 6px', borderBottom: `1px solid ${C.border}` }}>
                  <div style={{ fontSize: 10, color: C.dim, fontWeight: 600, letterSpacing: '0.06em', marginBottom: 8 }}>{t('shell.assistant.quickStart', 'INICIO RÁPIDO')}</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                    {[
                      t('shell.assistant.suggestions.s1', '¿Cómo uso {now_minus_7d} en los filtros?'),
                      t('shell.assistant.suggestions.s2', '¿Cómo configuro una programación?'),
                      t('shell.assistant.suggestions.s3', '¿Cómo envío correos por lotes desde una función?'),
                      t('shell.assistant.suggestions.s4', '¿Cómo hago referencia a la salida de un bloque anterior?'),
                    ].map((s) => (
                      <button key={s} onClick={() => {
                        const id = newConversation();
                        setView('chat');
                        setTimeout(() => { setInput(s); }, 50);
                      }} style={{
                        fontSize: 11, padding: '4px 9px', cursor: 'pointer',
                        backgroundColor: C.sidebar, color: C.muted,
                        border: `1px solid ${C.border}`, borderRadius: 12,
                      }}>
                        {s}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Conversation list */}
                <div style={{ padding: '8px 0' }}>
                  <div style={{ padding: '4px 16px 6px', fontSize: 10, color: C.dim, fontWeight: 600, letterSpacing: '0.06em' }}>{t('shell.assistant.conversations', 'CONVERSACIONES')}</div>
                  {conversations.map((c) => (
                    <div
                      key={c.id}
                      onClick={() => openChat(c.id)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 10,
                        padding: '10px 16px', cursor: 'pointer',
                        borderBottom: `1px solid ${C.border}`,
                        backgroundColor: activeId === c.id ? C.accentDim : 'transparent',
                      }}
                      onMouseEnter={(e) => { if (activeId !== c.id) (e.currentTarget as HTMLElement).style.backgroundColor = C.sidebar; }}
                      onMouseLeave={(e) => { if (activeId !== c.id) (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent'; }}
                    >
                      <MessageSquare size={14} color={activeId === c.id ? C.accent : C.dim} style={{ flexShrink: 0 }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, color: C.text, fontWeight: activeId === c.id ? 500 : 400, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {c.title === 'New conversation' ? t('shell.assistant.newConversation', 'Nueva conversación') : c.title}
                        </div>
                        <div style={{ fontSize: 10, color: C.dim, marginTop: 1 }}>
                          {t('shell.assistant.messagesCount', '{{count}} mensajes', { count: c.messages.length })} · {new Date(c.updatedAt).toLocaleDateString()}
                        </div>
                      </div>
                      <button
                        onClick={(e) => { e.stopPropagation(); deleteConversation(c.id); }}
                        style={{ background: 'none', border: 'none', color: C.dim, cursor: 'pointer', padding: 4, lineHeight: 0, flexShrink: 0 }}
                        onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.color = 'var(--color-status-red)'; }}
                        onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.color = C.dim; }}
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── Chat view ── */}
        {view === 'chat' && activeConvo && (
          <>
            {/* Actions panel */}
            {chatMode === 'actions' ? (
              <div style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 8, overflowY: 'auto', flex: 1 }}>
                <p style={{ fontSize: 11, color: 'var(--color-text-muted)', margin: '0 0 4px' }}>
                  {t('shell.assistant.actionsPanel.title', 'Acciones rápidas: haz clic para iniciar una conversación')}
                </p>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  {getAssistantActions(t).map(action => (
                    <ActionCard
                      key={action.title}
                      {...action}
                      onSelect={(prefill) => {
                        setInput(prefill);
                        setChatMode('help');
                        setTimeout(() => inputRef.current?.focus(), 50);
                      }}
                    />
                  ))}
                </div>
              </div>
            ) : (
              <div style={{ flex: 1, overflowY: 'auto', padding: '16px 16px 8px', display: 'flex', flexDirection: 'column', gap: 14 }}>

                {activeConvo.messages.length === 0 && (
                  <div style={{ textAlign: 'center', color: C.dim, padding: '40px 0' }}>
                    <MessageSquare size={28} color={C.border} style={{ marginBottom: 10 }} />
                    <div style={{ fontSize: 13 }}>{t('shell.assistant.emptyState.title', 'Inicia la conversación')}</div>
                    <div style={{ fontSize: 12, marginTop: 4 }}>{t('shell.assistant.emptyState.subtitle', 'Puedo ver tus funciones, programaciones y tipos de objetos en tiempo real.')}</div>
                  </div>
                )}

                {activeConvo.messages.map((m) => (
                  <div key={m.id} style={{ display: 'flex', gap: 10, alignItems: 'flex-start',
                    flexDirection: m.role === 'user' ? 'row-reverse' : 'row' }}>
                    {/* Avatar — a limit notice is the platform talking, not the model,
                        so it gets a warning mark instead of the assistant's "N". */}
                    <div style={{
                      width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
                      backgroundColor: m.kind === 'limit' ? mix.warnSoft : m.role === 'user' ? C.accentDim : C.dark,
                      border: `1px solid ${m.kind === 'limit' ? 'color-mix(in srgb, #FCD34D, var(--color-border) var(--tint-sink))' : m.role === 'user' ? `color-mix(in srgb, ${C.accent} 27%, transparent)` : C.darkBorder}`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 10, fontWeight: 700,
                      color: m.kind === 'limit' ? 'var(--color-warn)' : m.role === 'user' ? C.accent : '#A78BFA',
                    }}>
                      {m.kind === 'limit' ? <AlertTriangle size={13} /> : m.role === 'user' ? 'U' : 'N'}
                    </div>
                    <div style={{ maxWidth: '82%' }}>
                      <div style={{
                        backgroundColor: m.kind === 'limit' ? 'var(--color-warn-dim)' : m.role === 'user' ? C.accentDim : C.sidebar,
                        border: `1px solid ${m.kind === 'limit' ? 'var(--color-warn-border)' : m.role === 'user' ? `color-mix(in srgb, ${C.accent} 20%, transparent)` : C.border}`,
                        padding: '10px 13px', fontSize: 13, lineHeight: 1.55,
                        color: m.kind === 'limit' ? 'color-mix(in srgb, #78350F, white var(--hue-lift))' : C.text,
                      }}>
                        {m.kind === 'limit' && m.title && (
                          <div style={{ fontWeight: 600, marginBottom: 4, color: 'var(--color-warn)' }}>
                            {m.title}
                          </div>
                        )}
                        {m.kind === 'limit'
                          ? <span style={{ whiteSpace: 'pre-wrap' }}>{m.content}</span>
                          : m.role === 'assistant'
                            ? <Markdown text={m.content} />
                            : <span style={{ whiteSpace: 'pre-wrap' }}>{m.content}</span>}
                        {m.streaming && (
                          <span style={{
                            display: 'inline-block', width: 8, height: 14,
                            backgroundColor: 'var(--color-interactive)', marginLeft: 2,
                            animation: 'blink 1s step-end infinite',
                            verticalAlign: 'text-bottom',
                          }} />
                        )}
                        {(() => {
                          if (m.role !== 'assistant' || m.streaming || m.kind === 'limit') return null;
                          const guide = extractGuide(m.content);
                          return guide ? <AssistantGuideCard guide={guide} /> : null;
                        })()}
                        {(() => {
                          if (m.role !== 'assistant' || m.streaming || m.kind === 'limit') return null;
                          const action = extractAction(m.content);
                          if (!action) return null;
                          const status = actionStatuses[m.id] || 'pending';
                          return (
                            <ActionConfirmCard
                              action={action}
                              status={status}
                              onConfirm={() => executeAction(m.id, action)}
                              onReject={() => rejectAction(m.id)}
                            />
                          );
                        })()}
                      </div>
                      {m.role === 'assistant' && !m.streaming && m.kind !== 'limit' && (
                        <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
                          <button
                            onClick={() => setMessageFeedback(m.id, 'up')}
                            style={{
                              background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px',
                              fontSize: 12, opacity: m.feedback === 'up' ? 1 : 0.4,
                              color: m.feedback === 'up' ? '#16A34A' : 'var(--color-text-muted)',
                            }}
                            title={t('shell.assistant.feedback.helpful', 'Útil')}
                          >👍</button>
                          <button
                            onClick={() => setMessageFeedback(m.id, 'down')}
                            style={{
                              background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px',
                              fontSize: 12, opacity: m.feedback === 'down' ? 1 : 0.4,
                              color: m.feedback === 'down' ? 'var(--color-status-red)' : 'var(--color-text-muted)',
                            }}
                            title={t('shell.assistant.feedback.notHelpful', 'No es útil')}
                          >👎</button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}

                {loading && (
                  <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                    <div style={{
                      width: 28, height: 28, borderRadius: '50%', backgroundColor: C.dark,
                      border: `1px solid ${C.darkBorder}`, display: 'flex', alignItems: 'center',
                      justifyContent: 'center', flexShrink: 0,
                    }}>
                      <span style={{ fontSize: 10, fontWeight: 700, color: '#A78BFA' }}>N</span>
                    </div>
                    <div style={{ backgroundColor: C.sidebar, border: `1px solid ${C.border}`, padding: '10px 14px' }}>
                      <Loader size={14} color={C.accent} style={{ animation: 'spin 0.7s linear infinite' }} />
                    </div>
                  </div>
                )}

                <div ref={bottomRef} />
              </div>
            )}

            {/* Input area (always shown in chat view) */}
            <div style={{ padding: '10px 16px 14px', borderTop: `1px solid ${C.border}`, flexShrink: 0 }}>
              {/* Mode toggle */}
              <div style={{ display: 'flex', gap: 4, marginBottom: 8 }}>
                <button
                  onClick={() => setChatMode('help')}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 5,
                    height: 26, padding: '0 10px', borderRadius: 4, fontSize: 11, fontWeight: 500,
                    border: `1px solid ${chatMode === 'help' ? C.accent : C.border}`,
                    backgroundColor: chatMode === 'help' ? C.accentDim : 'transparent',
                    color: chatMode === 'help' ? C.accent : C.muted, cursor: 'pointer',
                  }}
                >
                  <HelpCircle size={11} /> {t('shell.assistant.modes.help', 'Ayuda de la Plataforma')}
                </button>
                <button
                  onClick={() => { setChatMode('data'); if (objectTypes.length === 0) fetchObjectTypes(); }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 5,
                    height: 26, padding: '0 10px', borderRadius: 4, fontSize: 11, fontWeight: 500,
                    border: `1px solid ${chatMode === 'data' ? C.accent : C.border}`,
                    backgroundColor: chatMode === 'data' ? C.accentDim : 'transparent',
                    color: chatMode === 'data' ? C.accent : C.muted, cursor: 'pointer',
                  }}
                >
                  <Database size={11} /> {t('shell.assistant.modes.data', 'Analizar Datos')}
                </button>
                <button
                  onClick={() => setChatMode('actions')}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 5,
                    height: 26, padding: '0 10px', borderRadius: 4, fontSize: 11, fontWeight: 500,
                    border: `1px solid ${chatMode === 'actions' ? C.accent : C.border}`,
                    backgroundColor: chatMode === 'actions' ? C.accentDim : 'transparent',
                    color: chatMode === 'actions' ? C.accent : C.muted, cursor: 'pointer',
                  }}
                >
                  <Zap size={11} /> {t('shell.assistant.modes.actions', 'Acciones')}
                </button>
              </div>

              {/* Object type selector (data mode only) */}
              {chatMode === 'data' && (
                <select
                  value={selectedObjectTypeId}
                  onChange={e => setSelectedObjectTypeId(e.target.value)}
                  style={{
                    width: '100%', height: 30, marginBottom: 8, padding: '0 8px',
                    border: `1px solid ${C.border}`, borderRadius: 4,
                    fontSize: 12, color: selectedObjectTypeId ? C.text : C.dim,
                    backgroundColor: C.bg,
                  }}
                >
                  <option value="">{t('shell.assistant.dataMode.selectObjectType', 'Selecciona un tipo de objeto a analizar…')}</option>
                  {pickableObjectTypes.map(ot => (
                    <option key={ot.id} value={ot.id}>{ot.displayName || ot.name}</option>
                  ))}
                </select>
              )}

              {chatMode !== 'actions' && (
                <>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                    <textarea
                      ref={inputRef}
                      value={input}
                      onChange={(e) => setInput(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
                      placeholder={chatMode === 'data'
                        ? t('shell.assistant.dataMode.placeholder', 'Haz una pregunta sobre tus datos… (ej. "¿Cuántos registros tienen estado pendiente?")')
                        : t('shell.assistant.helpMode.placeholder', 'Pregunta lo que sea sobre tu configuración de Nexus... (Enter para enviar)')}
                      rows={2}
                      style={{
                        flex: 1, resize: 'none', padding: '9px 12px', fontSize: 13,
                        backgroundColor: C.bg, border: `1px solid ${C.border}`,
                        color: C.text, outline: 'none', fontFamily: 'inherit', lineHeight: 1.5,
                      }}
                    />
                    <button
                      onClick={send}
                      disabled={loading || !input.trim() || (chatMode === 'data' && !selectedObjectTypeId)}
                      style={{
                        width: 38, height: 38, flexShrink: 0,
                        backgroundColor: (input.trim() && !loading && (chatMode !== 'data' || selectedObjectTypeId)) ? C.accent : C.sidebar,
                        color: (input.trim() && !loading && (chatMode !== 'data' || selectedObjectTypeId)) ? '#fff' : C.dim,
                        border: 'none', cursor: (input.trim() && !loading && (chatMode !== 'data' || selectedObjectTypeId)) ? 'pointer' : 'default',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        transition: 'background-color 0.15s',
                      }}
                    >
                      <Send size={15} />
                    </button>
                  </div>
                  <div style={{ fontSize: 10, color: C.dim, marginTop: 5 }}>
                    {chatMode === 'data'
                      ? t('shell.assistant.dataMode.footer', 'Obtiene hasta 50 registros y solicita el análisis a Claude')
                      : t('shell.assistant.helpMode.footer', 'Consciente del contexto: ve tus funciones, programaciones y ontología en tiempo real')}
                  </div>
                </>
              )}
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default NexusAssistant;
